import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Building2, Plus, Pencil } from "lucide-react";

const EMPTY = { name: "", slug: "", logo_url: "", admin_name: "", admin_email: "", admin_password: "", country_code: "DE" };

export default function Tenants() {
  const { user } = useAuth();
  const { t, fmtCountry } = useI18n();
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [creating, setCreating] = useState(null);   // create form state or null
  const [editing, setEditing] = useState(null);     // {id,name,logo_url,active}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/auth/tenants").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/tenant-config/registry").then((r) => setCountries(r.data.countries || [])).catch(() => {}); }, []);

  if (user?.platform_role !== "superadmin") {
    return <EmptyState testId="tenants-forbidden" icon={Building2} title={t("ten.noAccess")} hint={t("ten.noAccessHint")} />;
  }

  const createTenant = async () => {
    setBusy(true);
    try {
      await api.post("/auth/tenants", creating);
      toast.success(t("ten.created")); setCreating(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/auth/tenants/${editing.id}`, { name: editing.name, logo_url: editing.logo_url, active: editing.active });
      toast.success(t("ten.updated")); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <PageHeader title={t("ten.title")} subtitle={t("ten.subtitle")} />
        <Button className="w-full sm:w-auto" onClick={() => setCreating({ ...EMPTY })} data-testid="tenant-create-btn"><Plus className="h-4 w-4 mr-1.5" /> {t("ten.create")}</Button>
      </div>

      {rows.length === 0 ? <EmptyState testId="tenants-empty" icon={Building2} title={t("ten.empty")} /> : (
        <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm" data-testid="tenants-table">
          <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("common.name")}</Th><Th>Slug</Th><Th right>{t("common.users")}</Th><Th>{t("common.status")}</Th><Th right>{t("common.action")}</Th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.id} className="hover:bg-muted/40" data-testid={`tenant-row-${row.slug}`}>
                <Td><div className="flex items-center gap-2">{row.logo_url ? <img src={row.logo_url} alt="" className="h-6 w-6 rounded object-contain" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}<span className="font-medium">{row.name}</span></div></Td>
                <Td><span className="font-mono text-xs">{row.slug}</span></Td>
                <Td right mono>{row.user_count ?? 0}</Td>
                <Td>{row.active === false ? <span className="text-xs text-destructive">{t("common.inactive")}</span> : <span className="text-xs text-chart-2">{t("common.active")}</span>}</Td>
                <Td right><Button size="sm" variant="outline" onClick={() => setEditing({ id: row.id, name: row.name, logo_url: row.logo_url || "", active: row.active !== false })} data-testid={`tenant-edit-${row.slug}`}><Pencil className="h-3.5 w-3.5" /></Button></Td>
              </tr>))}
            </tbody></table>
        </div>
      )}

      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("ten.createTitle")}</DialogTitle></DialogHeader>
          {creating && <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">{t("ten.company")}</label><Input value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="tenant-name" /></div>
              <div><label className="text-xs text-muted-foreground">{t("ten.slug")}</label><Input value={creating.slug} onChange={(e) => setCreating({ ...creating, slug: e.target.value })} placeholder={t("ten.slugPh")} data-testid="tenant-slug" /></div>
            </div>
            <div><label className="text-xs text-muted-foreground">{t("ten.logoUrl")}</label><Input value={creating.logo_url} onChange={(e) => setCreating({ ...creating, logo_url: e.target.value })} data-testid="tenant-logo" /></div>
            <div>
              <label className="text-xs text-muted-foreground">{t("ten.countryLabel")}</label>
              <Select value={creating.country_code} onValueChange={(v) => setCreating({ ...creating, country_code: v })}>
                <SelectTrigger className="mt-1" data-testid="tenant-country"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {countries.map((c) => (
                    <SelectItem key={c.country_code} value={c.country_code} data-testid={`tenant-country-${c.country_code}`}>
                      {fmtCountry(c.country_code)} ({c.country_code}) · {c.default_currency} · {c.default_standard_tax_rate}%
                    </SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-border pt-3 text-sm font-medium">{t("ten.admin")}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">{t("common.name")}</label><Input value={creating.admin_name} onChange={(e) => setCreating({ ...creating, admin_name: e.target.value })} data-testid="tenant-admin-name" /></div>
              <div><label className="text-xs text-muted-foreground">{t("common.email")}</label><Input type="email" value={creating.admin_email} onChange={(e) => setCreating({ ...creating, admin_email: e.target.value })} data-testid="tenant-admin-email" /></div>
            </div>
            <div><label className="text-xs text-muted-foreground">{t("ten.passwordMin")}</label><Input type="password" value={creating.admin_password} onChange={(e) => setCreating({ ...creating, admin_password: e.target.value })} data-testid="tenant-admin-password" /></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>{t("common.cancel")}</Button>
            <Button onClick={createTenant} disabled={busy} data-testid="tenant-save-btn">{busy ? t("ten.creating") : t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("ten.editTitle")}</DialogTitle></DialogHeader>
          {editing && <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground">{t("common.name")}</label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="tenant-edit-name" /></div>
            <div><label className="text-xs text-muted-foreground">{t("ten.logoUrl")}</label><Input value={editing.logo_url} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} data-testid="tenant-edit-logo" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="tenant-edit-active" /> {t("common.active")}</label>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="tenant-edit-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
